import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { revokeDeviceFromEmailToken } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ShieldCheck, AlertTriangle, Lock } from "lucide-react";

// One-tap revoke target for the "Sign that device out" button inside a
// new-country login alert email. Auto-consumes the `?token=` query param.
// Sister page to /revert-email-change/:token.
export default function RevokeDevice() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [state, setState] = useState("loading"); // loading | success | already | error
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!token) {
                if (!cancelled) {
                    setError("Missing token in link.");
                    setState("error");
                }
                return;
            }
            try {
                const r = await revokeDeviceFromEmailToken(token);
                if (!cancelled) {
                    if (r?.already_revoked) {
                        setState("already");
                    } else {
                        setState("success");
                    }
                    track("login_alert_session_revoked");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.response?.data?.detail
                            || "This revoke link is invalid or has expired.",
                    );
                    setState("error");
                }
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div
                    className="bl-container py-20 max-w-md text-center"
                    data-testid="revoke-device-page"
                >
                    {state === "loading" && (
                        <>
                            <div className="bl-spinner mx-auto mb-4" />
                            <p className="text-bl-muted">Signing that device out…</p>
                        </>
                    )}
                    {(state === "success" || state === "already") && (
                        <div data-testid="revoke-success">
                            <div className="mx-auto w-14 h-14 rounded-full bg-bl-primary/10 flex items-center justify-center">
                                <ShieldCheck className="w-7 h-7 text-bl-primary" />
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                                {state === "already"
                                    ? "Already signed out."
                                    : "Device signed out."}
                            </h1>
                            <p className="mt-3 text-bl-muted leading-relaxed">
                                {state === "already"
                                    ? "That session was already revoked. Your account is safe — no further action needed."
                                    : "The device you didn’t recognize has been signed out of your account. You’re still signed in everywhere else."}
                            </p>
                            <div className="mt-6 rounded-lg border border-bl bg-bl-cream px-4 py-3 text-left">
                                <div className="flex items-start gap-2">
                                    <Lock className="w-4 h-4 text-bl-primary mt-0.5 shrink-0" />
                                    <p className="text-xs text-bl-muted leading-relaxed">
                                        <span className="font-medium text-bl">
                                            Reset your password now.
                                        </span>{" "}
                                        Whoever got into your account likely had your password
                                        too. A reset locks them out for good.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex flex-col gap-2">
                                <Link
                                    to="/forgot-password"
                                    className="bl-btn-primary"
                                    data-testid="revoke-success-reset-cta"
                                >
                                    Reset password
                                </Link>
                                <Link
                                    to="/account-settings#account-activity-section"
                                    className="text-sm text-bl-muted hover:text-bl"
                                    data-testid="revoke-success-activity-cta"
                                >
                                    Review recent account activity
                                </Link>
                            </div>
                        </div>
                    )}
                    {state === "error" && (
                        <div data-testid="revoke-error">
                            <div className="mx-auto w-14 h-14 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center">
                                <AlertTriangle className="w-7 h-7 text-rose-700" />
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                                Could not revoke.
                            </h1>
                            <p className="mt-3 text-bl-muted leading-relaxed">
                                {error}
                            </p>
                            <p className="mt-6 text-sm text-bl-muted">
                                Sign in and revoke the device from{" "}
                                <Link
                                    to="/account-settings#account-activity-section"
                                    className="text-bl-primary hover:underline"
                                >
                                    Account Settings → Where you’re signed in
                                </Link>
                                .
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
